import { EmailTrigger } from "@/types/enums/EmailTrigger";
import { GuestState } from "@/types/enums/GuestState";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { GuestType } from "@/types/enums/GuestType";

/**
 * Client mirror of `GuestService.deriveLifecycleTriggers`
 * (main-backend/src/main/java/org/nikita/app/guest/GuestService.java:3316-3340).
 *
 * The backend derives these from the guest DIFF, never from the caller's
 * trigger argument, and fires them IN ADDITION to it (`GuestService.java:2902-2917`).
 * The frontend needs the same answer for one reason only: to decide whether an
 * automation is about to change a guest behind our back, so the surface can
 * reconcile instead of showing a stale row. It is a gate, never a promise —
 * whether the workflow's action actually lands is the server's call.
 *
 * The two columns mean different things and must not be conflated:
 *  - `state` is the GUEST's own answer (what the event funnel counts);
 *  - `acceptanceState` is the ORGANISER's verdict on a pending request.
 *
 * KNOWN DIVERGENCES — deliberately not ported, because they depend on state the
 * browser cannot see. Both make us over-predict, which costs one reconcile wave
 * that finds nothing; under-predicting would leave the row stale, which is the
 * bug this exists to fix.
 *  - The waitlist-promotion sync (`GuestService.java:2410-2416`) rewrites
 *    `acceptanceState` to ACCEPTED before the derive runs, so a plain `state`
 *    write can produce REQUEST_APPROVED / COMPANION_APPROVED that this mirror
 *    does not report.
 *    CAUTION — an earlier version of this note claimed the sync was harmless
 *    "because a waitlisted guest is stored as SYSTEM". That is true of the
 *    CREATE paths only, not of the stored state: the guest-detail status
 *    dropdown sends `state` alone and leaves `acceptanceState = PENDING`
 *    (which is precisely why the sync exists — `GuestService.java:2401-2409`),
 *    and `GuestRepository.java` documents PENDING+WAITLISTED rows as a live
 *    shape. Omitting the sync here therefore UNDER-predicts rather than being
 *    harmless. It stays omitted because under-predicting a reconcile costs one
 *    stale row; `lifecycle-cofire.ts` deliberately does NOT inherit the
 *    omission, because there the same gap would hide a duplicate email.
 *  - The `forceWaitlist` reroute (`GuestService.java:2435-2443`) rewrites an
 *    approval into `state=WAITLISTED, acceptanceState=SYSTEM`, which fires
 *    nothing. Only reachable when the rules engine blocks, which is a live
 *    server decision, so an approval resubmitted as "waitlist" over-predicts
 *    REQUEST_APPROVED here.
 */

/** The only three columns the derivation reads. */
export type LifecycleSlice = {
  state?: GuestState | null;
  acceptanceState?: AcceptanceState | null;
  guestType?: GuestType | string | null;
};

/**
 * @param previous the STORED guest, as last read from the server.
 * @param current  the PARTIAL UPDATE PAYLOAD — what is actually being PUT, not
 *   the merged result. The Java reads the same partial (call site
 *   `GuestService.java:2912`), which is why a column that is absent from the
 *   payload fires nothing even when its stored value differs.
 */
export function deriveLifecycleTriggers(
  previous: LifecycleSlice | null | undefined,
  current: LifecycleSlice | null | undefined,
  /**
   * The admin's explicit "run the approval/denial automations again" opt-in for
   * a decision that was ALREADY made. Verbatim mirror of the Java parameter —
   * never inferred, never defaulted to true. Only a RE-decision honours it: a
   * write out of SYSTEM stays waitlist bookkeeping and a write to PENDING
   * announces nothing, both regardless of the flag.
   */
  retriggerAutomations: boolean = false,
): EmailTrigger[] {
  const out: EmailTrigger[] = [];
  if (previous == null || current == null) return out;

  const newState = current.state;
  // The `!= null` half is redundant by construction — null equals neither
  // ACCEPTED nor DECLINED, so dropping it changes no outcome, and no test on
  // either side can red it. Kept for verbatim parity with the Java, which
  // carries the same redundant guard.
  if (newState != null && newState !== previous.state) {
    if (newState === GuestState.ACCEPTED) out.push(EmailTrigger.GUEST_ACCEPTED);
    else if (newState === GuestState.DECLINED) out.push(EmailTrigger.GUEST_DECLINED);
  }

  const newAcceptance = current.acceptanceState;
  // A decision is announced when it is the organiser's FIRST verdict on an open
  // request, or when they re-decide one and explicitly ask for the automations
  // to run again. Keying on the SOURCE is what keeps waitlist bookkeeping
  // (SYSTEM → ACCEPTED) from being announced as an approval — the flag cannot
  // buy that lie either, which is why the SYSTEM check sits ahead of it. A
  // write to PENDING announces nothing: there is no "request reopened" trigger,
  // and mapping it onto a denial would be the same lie.
  // Keyed on the DESTINATION first, mirroring the Java classifier. A verdict is
  // only ever ACCEPTED or DECLINED; every other target is bookkeeping. The Java
  // side once expressed this as an `else` and swallowed SYSTEM, turning "park on
  // the waitlist" into a rejection email — the shape below cannot do that.
  //
  const isVerdict =
    newAcceptance === AcceptanceState.ACCEPTED ||
    newAcceptance === AcceptanceState.DECLINED;
  // Exactly the Java condition, and nothing more. An earlier version also
  // counted a MISSING stored acceptanceState as a first verdict, on the theory
  // that over-predicting only costs a wasted reconcile. But the backend fires
  // nothing at all for that row (`firstVerdict = oldAcceptance == PENDING`), so
  // predicting nothing is not under-predicting — it is the right answer, and
  // the extra clause made this file disagree with the Java in 1296 of the
  // enumerated state pairs while its own docstring called itself a verbatim
  // mirror.
  const firstVerdict = previous.acceptanceState === AcceptanceState.PENDING;
  // The re-announcement arm is the Java's `replacesVerdict`, and it needs the
  // same precondition the Java states: the row must ALREADY carry a verdict.
  // Written as "anything that is not PENDING or SYSTEM", this arm quietly
  // re-opened the missing-acceptanceState case the comment above says was
  // removed — the mirror predicted an approval for a row the backend announces
  // nothing for. Over-predicting only costs a wasted reconcile wave, so this
  // was never visible; it was still the file disagreeing with the thing it
  // calls itself a verbatim mirror of.
  const replacesVerdict =
    retriggerAutomations &&
    (previous.acceptanceState === AcceptanceState.ACCEPTED ||
      previous.acceptanceState === AcceptanceState.DECLINED);
  const announcesDecision =
    isVerdict &&
    newAcceptance !== previous.acceptanceState &&
    previous.acceptanceState !== AcceptanceState.SYSTEM &&
    (firstVerdict || replacesVerdict);
  if (announcesDecision) {
    // The payload's guestType describes the row AFTER the write, so it wins;
    // the stored value is the fallback when the update doesn't re-type.
    const type = current.guestType != null ? current.guestType : previous.guestType;
    const companion = type === GuestType.COMPANION;
    if (newAcceptance === AcceptanceState.ACCEPTED) {
      out.push(companion ? EmailTrigger.COMPANION_APPROVED : EmailTrigger.REQUEST_APPROVED);
    } else if (newAcceptance === AcceptanceState.DECLINED) {
      out.push(companion ? EmailTrigger.COMPANION_DENIED : EmailTrigger.REQUEST_DENIED);
    }
  }
  return out;
}

/**
 * The explicit trigger a batch update carries, mirroring
 * `GuestService.java:2902-2905`: an archive-bearing update passes `null`, so it
 * fires NO `GUEST_UPDATED` — while its derived lifecycle triggers still fire.
 */
export function explicitUpdateTrigger(
  payload: { archived?: boolean | null } | null | undefined,
): EmailTrigger | null {
  if (payload?.archived != null) return null;
  return EmailTrigger.GUEST_UPDATED;
}

/**
 * The create-path split, mirroring `GuestService.java:3459-3473`: a guest that
 * lands PENDING is a request, not a creation. Callers that hardcode
 * `GUEST_CREATED` mispredict every `/create-pending` and
 * `/create-companion-request` write.
 */
export function deriveCreateTrigger(
  acceptanceState: AcceptanceState | null | undefined,
): EmailTrigger | null {
  if (acceptanceState === AcceptanceState.PENDING) return EmailTrigger.REQUEST_SUBMITTED;
  if (
    acceptanceState === AcceptanceState.ACCEPTED ||
    acceptanceState === AcceptanceState.SYSTEM
  ) {
    return EmailTrigger.GUEST_CREATED;
  }
  return null;
}
